import { getSupabaseClient, type DdmSupabaseClient } from './supabase';

export type DealSlugInput = {
  zone: string;
  building?: string | null;
  community?: string | null;
  bedrooms: number;
  discountPct?: number | null;
};

export type GenerateDealSlugOptions = {
  /** UUID of the deal we're slugging — used as the collision suffix source. */
  dealId: string;
  client?: DdmSupabaseClient;
};

const STUDIO_LABEL = 'studio';
const NO_DISCOUNT_LABEL = 'no-discount';

function slugify(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function bedroomsLabel(bedrooms: number): string {
  if (bedrooms <= 0) return STUDIO_LABEL;
  return `${bedrooms}br`;
}

function discountLabel(discountPct: number | null | undefined): string {
  if (discountPct === null || discountPct === undefined) return NO_DISCOUNT_LABEL;
  const abs = Math.round(Math.abs(discountPct));
  if (abs <= 0) return NO_DISCOUNT_LABEL;
  return `${abs}-discount`;
}

/**
 * Build the canonical Spec V1.1 §P6 slug from a deal's metadata.
 * Pattern: {zone}-{building|community}-{bedrooms}br-{discount}-discount.
 *
 * Building is preferred over community when present; community is the
 * fallback so deals that don't sit inside a named tower still slug
 * deterministically. The discount segment falls back to `no-discount`
 * when discount_pct is NULL (DLD recalc hasn't run yet) or zero.
 *
 * Pure — no DB call. Use {@link resolveDealSlug} when you need
 * collision handling against the live deals table.
 */
export function buildDealSlug(input: DealSlugInput): string {
  const place = input.building?.trim() || input.community?.trim() || '';
  const parts = [
    slugify(input.zone),
    slugify(place),
    bedroomsLabel(input.bedrooms),
    discountLabel(input.discountPct),
  ].filter((segment) => segment.length > 0);
  return parts.join('-');
}

/**
 * Resolve a deals.slug value safe to write back to the table.
 *
 * Picks the canonical slug from {@link buildDealSlug}, then queries
 * `deals` for a colliding row. If the only conflict is the deal we're
 * about to update (its own current slug) we return the canonical form
 * unchanged. Otherwise we append `-{dealId.slice(0,8)}` so the slug
 * remains unique without burning the canonical pattern for the next
 * deal in the same building.
 *
 * Authorization: relies on the public `deals_select` RLS clause —
 * admins see every status. anon would only see published deals,
 * which would risk false negatives, but this helper is meant to be
 * called from the back-office (admin/super_admin context).
 */
export async function resolveDealSlug(
  input: DealSlugInput,
  options: GenerateDealSlugOptions,
): Promise<string> {
  const client = options.client ?? getSupabaseClient();
  const canonical = buildDealSlug(input);

  if (canonical.length === 0) {
    // Defensive — should never happen for a deal that satisfies the
    // column constraints (zone NOT NULL, bedrooms NOT NULL).
    return `deal-${options.dealId.slice(0, 8)}`;
  }

  const { data, error } = await client.from('deals').select('id').eq('slug', canonical).limit(1);
  if (error) throw error;

  const conflict = data?.[0];
  if (!conflict || conflict.id === options.dealId) {
    return canonical;
  }
  return `${canonical}-${options.dealId.slice(0, 8)}`;
}
